import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChartPieSlice } from "@phosphor-icons/react";
import { getStats } from "@/lib/api";
import SectionEyebrow from "@/components/shared/SectionEyebrow";

const SIZE = 220;
const STROKE = 28;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export default function ServiceMix() {
  const [mix, setMix] = useState([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const s = await getStats();
        if (!mounted) return;
        const arr = s.service_mix || [];
        setMix(arr);
        setTotal(arr.reduce((a, b) => a + b.count, 0));
      } catch {
        /* noop */
      }
    };
    load();
    const id = setInterval(load, 25000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  if (!mix.length) return null;

  // Build arc segments
  let offset = 0;
  const segments = mix.map((s) => {
    const portion = total > 0 ? s.count / total : 0;
    const length = portion * CIRCUMFERENCE;
    const seg = { ...s, portion, length, offset };
    offset += length;
    return seg;
  });

  const leadKey = [...mix].sort((a, b) => b.count - a.count)[0];
  const leadPct = total > 0 ? Math.round((leadKey.count / total) * 100) : 0;

  return (
    <section className="relative py-24 border-t border-ih-line" data-testid="home-service-mix">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5 space-y-5">
          <SectionEyebrow label="Service Mix" color="electric" />
          <h2 className="font-display font-extrabold text-4xl lg:text-5xl text-white leading-[1.05]">
            Where Caribbean<br />
            demand is <span className="text-ih-teal">building.</span>
          </h2>
          <p className="text-white/60 leading-relaxed max-w-md">
            Live breakdown of pre-launch interest across the five IslandHop verticals.
            Pulled from driver, service-pro and merchant applications in real time.
          </p>
          {total > 0 && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-ih-line2 bg-white/[0.03]">
              <ChartPieSlice size={14} className="text-ih-teal" weight="duotone" />
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/60">
                Leading vertical:&nbsp;
                <span className="text-white">{leadKey.label}</span>
                &nbsp;· {leadPct}%
              </span>
            </div>
          )}
        </div>

        <div className="lg:col-span-7 grid sm:grid-cols-[auto,1fr] gap-10 items-center">
          {/* Donut */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative mx-auto"
            style={{ width: SIZE, height: SIZE }}
            data-testid="service-mix-donut"
          >
            <svg width={SIZE} height={SIZE} className="-rotate-90">
              {/* background ring */}
              <circle
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                stroke="rgba(255,255,255,0.06)"
                strokeWidth={STROKE}
                fill="none"
              />
              {total > 0 && segments.map((seg) => (
                <circle
                  key={seg.key}
                  cx={SIZE / 2}
                  cy={SIZE / 2}
                  r={RADIUS}
                  stroke={seg.color}
                  strokeWidth={STROKE}
                  fill="none"
                  strokeDasharray={`${seg.length} ${CIRCUMFERENCE - seg.length}`}
                  strokeDashoffset={-seg.offset}
                  strokeLinecap="butt"
                />
              ))}
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/40">Total signals</span>
              <span className="font-display font-extrabold text-5xl text-white tabular-nums leading-none mt-1">{total}</span>
            </div>
          </motion.div>

          {/* Legend */}
          <div className="space-y-2.5" data-testid="service-mix-legend">
            {segments.map((s) => {
              const pct = total > 0 ? Math.round(s.portion * 100) : 0;
              return (
                <div key={s.key} className="flex items-center gap-3 bg-ih-surface border border-ih-line rounded-xl px-4 py-3" data-testid={`mix-row-${s.key}`}>
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s.color }} />
                  <span className="text-sm text-white/85 flex-1">{s.label}</span>
                  <span className="text-sm text-white font-mono tabular-nums w-10 text-right">{s.count}</span>
                  <span className="text-xs text-white/45 font-mono tabular-nums w-12 text-right">{pct}%</span>
                  <div className="hidden sm:block w-24 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: s.color, transition: "width 600ms ease-out" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
